import { useState, useEffect, useCallback, useMemo } from 'react';
import { getTagsTree, createTag, updateTag, deleteTag, importProgram, uploadProgram } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, BookOpen, Tag, Loader2, X, Upload, Sparkles, FileUp } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { TagRow, TYPE_META, flattenTree } from '../components/TagNode';

export default function TagsManager() {
  usePageTitle('Notions pédagogiques');
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [newSubject, setNewSubject] = useState('');
  const [addingChild, setAddingChild] = useState(null);
  const [childLabel, setChildLabel] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importSubject, setImportSubject] = useState('');
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);

  const loadTree = useCallback(async () => {
    try {
      const res = await getTagsTree();
      setTree(res.data || []);
      const exp = {};
      for (const n of (res.data || [])) exp[n.tag_id] = true;
      setExpanded(prev => ({ ...prev, ...exp }));
    } catch (e) { console.error('Failed to load tag tree:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadTree(); }, [loadTree]);

  const toggleExpand = (tagId) => setExpanded(prev => ({ ...prev, [tagId]: !prev[tagId] }));

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.trim()) return;
    try {
      await createTag({ label: newSubject.trim(), type: 'subject' });
      setNewSubject('');
      toast.success('Matière créée');
      loadTree();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleAddChild = (parentId, type) => {
    setAddingChild({ parentId, type });
    setChildLabel('');
    setExpanded(prev => ({ ...prev, [parentId]: true }));
  };

  const handleConfirmAddChild = async (e) => {
    e.preventDefault();
    if (!childLabel.trim() || !addingChild) return;
    try {
      await createTag({ label: childLabel.trim(), type: addingChild.type, parent_id: addingChild.parentId });
      setAddingChild(null);
      setChildLabel('');
      const meta = TYPE_META[addingChild.type];
      toast.success((meta ? meta.label : 'Élément') + ' créé');
      loadTree();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleRename = async (tagId, newLabel) => {
    try {
      await updateTag(tagId, { label: newLabel });
      toast.success('Renommé');
      loadTree();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTag(deleteTarget.tag_id);
      toast.success('Supprimé');
      setDeleteTarget(null);
      loadTree();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importSubject.trim()) return;
    if (!importText.trim() && !pdfFile) return;
    setImporting(true);
    try {
      let res;
      if (pdfFile) {
        const formData = new FormData();
        formData.append('file', pdfFile);
        formData.append('subject', importSubject.trim());
        res = await uploadProgram(formData);
      } else {
        res = await importProgram({ subject: importSubject.trim(), text: importText.trim() });
      }
      toast.success(res.data.message);
      setImportSubject('');
      setImportText('');
      setPdfFile(null);
      setShowImport(false);
      loadTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'import');
    }
    setImporting(false);
  };

  // Build a flat lookup map for parent visibility checks
  const allNodesMap = useMemo(() => {
    const map = {};
    function buildMap(nodes) {
      for (var i = 0; i < nodes.length; i++) {
        map[nodes[i].tag_id] = nodes[i];
        if (nodes[i].children) buildMap(nodes[i].children);
      }
    }
    buildMap(tree);
    return map;
  }, [tree]);

  const visibleItems = useMemo(() => {
    return flattenTree(tree, 0).filter(function(item) {
      var node = item.node;
      var parentId = node.parent_id;
      while (parentId) {
        if (!expanded[parentId]) return false;
        var parent = allNodesMap[parentId];
        parentId = parent ? parent.parent_id : null;
      }
      return true;
    });
  }, [tree, expanded, allNodesMap]);

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const childMeta = addingChild ? (TYPE_META[addingChild.type] || TYPE_META.notion) : null;
  const ChildIcon = childMeta ? childMeta.icon : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tags-manager-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight">Notions pédagogiques</h1>
        <p className="text-muted-foreground mt-1">Organisez votre arbre de notions : Matière → Thème → Section → Notion</p>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <form onSubmit={handleAddSubject} className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-blue-400 shrink-0" />
            <Input value={newSubject} onChange={e => setNewSubject(e.target.value)} placeholder="Ajouter une matière (ex: Mathématiques, SES, SVT...)" className="flex-1 h-9" data-testid="add-subject-input" />
            <Button type="submit" size="sm" disabled={!newSubject.trim()} className="bg-primary text-primary-foreground" data-testid="add-subject-btn">
              <Plus className="w-4 h-4 mr-1" /> Matière
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => setShowImport(!showImport)} data-testid="toggle-import-btn" className="border border-white/5">
              <Sparkles className="w-4 h-4 mr-1" /> Importer un programme
            </Button>
          </form>
        </CardContent>
      </Card>

      {showImport && (
        <Card className="bg-card border-border border-primary/20" data-testid="import-program-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4 text-primary" /> Importer depuis un programme officiel</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleImport} className="space-y-3">
              <Input value={importSubject} onChange={e => setImportSubject(e.target.value)} placeholder="Nom de la matière (ex: SES, Mathématiques, SVT...)" className="h-9" data-testid="import-subject-input" />
              <div className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-border bg-muted/10">
                <FileUp className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1">
                  <label className="cursor-pointer">
                    <span className="text-sm text-primary hover:underline">{pdfFile ? pdfFile.name : 'Choisir un fichier PDF'}</span>
                    <input type="file" accept=".pdf" className="hidden" data-testid="import-file-input" onChange={e => { setPdfFile(e.target.files[0] || null); if (e.target.files[0]) setImportText(''); }} />
                  </label>
                  <p className="text-xs text-muted-foreground mt-0.5">Programme officiel ou bulletin officiel (PDF, max 10 Mo)</p>
                </div>
                {pdfFile && <Button type="button" variant="ghost" size="sm" onClick={() => setPdfFile(null)}><X className="w-3.5 h-3.5" /></Button>}
              </div>
              {!pdfFile && (
                <textarea value={importText} onChange={e => setImportText(e.target.value)}
                  placeholder="Ou collez ici le contenu du programme (thèmes, sections, notions)..."
                  className="w-full h-32 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="import-text-input" />
              )}
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">L'IA analysera le contenu et créera automatiquement les thèmes, sections et notions.</p>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setShowImport(false); setPdfFile(null); }}>Annuler</Button>
                  <Button type="submit" size="sm" disabled={!importSubject.trim() || (!importText.trim() && !pdfFile) || importing} className="bg-primary text-primary-foreground" data-testid="import-submit-btn">
                    {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    {importing ? 'Analyse en cours...' : 'Analyser et créer'}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {addingChild && ChildIcon && (
        <Card className="bg-card border-border border-primary/30">
          <CardContent className="p-4">
            <form onSubmit={handleConfirmAddChild} className="flex items-center gap-3">
              <ChildIcon className={'w-5 h-5 shrink-0 ' + childMeta.cls.split(' ')[0]} />
              <Input value={childLabel} onChange={e => setChildLabel(e.target.value)} placeholder={'Nom du ' + childMeta.label.toLowerCase() + '...'} className="flex-1 h-9" autoFocus data-testid="add-child-input" />
              <Button type="submit" size="sm" disabled={!childLabel.trim()} className="bg-primary text-primary-foreground" data-testid="add-child-confirm">
                <Plus className="w-4 h-4 mr-1" /> Créer
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setAddingChild(null)} data-testid="add-child-cancel">
                <X className="w-4 h-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {tree.length > 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-0.5">
            {visibleItems.map(function(item) {
              var meta = TYPE_META[item.node.type] || TYPE_META.notion;
              return (
                <TagRow key={item.node.tag_id} node={item.node} depth={item.depth} meta={meta} isOpen={!!expanded[item.node.tag_id]} onToggle={toggleExpand} onAdd={handleAddChild} onRename={handleRename} onDelete={setDeleteTarget} />
              );
            })}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="text-center py-16">
            <Tag className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
            <p className="text-muted-foreground">Aucune notion pédagogique créée.</p>
            <p className="text-xs text-muted-foreground mt-1">Commencez par ajouter une matière ci-dessus.</p>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground text-center">
        Les notions associées aux questions de vos QCM alimentent le suivi pédagogique de vos élèves.
      </p>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && ('« ' + deleteTarget.label + ' » sera supprimé. Cette action est irréversible.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-tag">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
